import React, { useEffect, useState } from 'react';
import './ai-modify-image.less';
import classNames from 'classnames';
import dayjs from 'dayjs';
import saveAs from 'file-saver';
import { Message, Scrollbars, SliderInput } from '@meitu/saas-ui';
import { parseLayerId } from '@meitu/saas-editor-core/es/framework';
import { base64Kit } from '@meitu/saas-editor-core/es/utils/base64-kit';
import { imageConverter, loadImageAsync } from '@meitu/saas-editor-core/es/utils/file-kit';
import { uploadBase64ToObs } from '@meitu/saas-editor-core/es/utils/upload-kit';
import { IImageLayerProps, LayerType } from '@meitu/saas-editor-core/es/version-types';
import IconFont from '@meitu/xdesign-icon-font';
import GenBtn from '@meitu/xdesign-widgets/dist/meidouInfo/compoments/genBtn';
import { useUserInfoHelper } from '@/editor/header/components/user-info/hooks/use-user-info-helper';
import { events, PLUGIN_CLOSE_EVENT } from '@/events-bus';
import { useLocaleStore } from '@/i18n';
import { editor } from '@/instance';
import { getAiModifyTask, getAiTaskQuery } from '@/services/ai';
import { useStoreActions, useStoreState } from '@/store';
import { AIModifyImagePainType, DefaultAIModifyImageInfo } from '@/store/models/plugin-model';
import { getCurrentScene } from '@/utils/track';
import { AiTip } from '../../components/ai-tip/ai-tip';
import ErrorItem from '../ai-expend-image/error-item';
import BackTitle from '../back-title/back-title';
import aiModifyImageMp4 from './assets/ai-modify-image.mp4';
import { clearCanvas } from './operate/use-pain';

export const AI_MODIFY_IMAGE = 'image_manipulation';

const AIModifyImage = () => {
    const { $t } = useLocaleStore();
    const [resultList, setResultList] = useState<string[]>([]);
    const [generating, setGenerating] = useState(false);

    const aiModifyImageInfo = useStoreState((state) => state.pluginModel.aiModifyImageInfo);
    const setAIModifyImageInfo = useStoreActions((actions) => actions.pluginModel.setAIModifyImageInfo);
    const funcUsedMapState = useStoreState((state) => state.pluginModel.funcUsedMap);
    const setFuncUsedMapAction = useStoreActions((actions) => actions.pluginModel.setFuncUsedMap);
    const { desc, painType, painSize } = aiModifyImageInfo;

    const setIsAiModifyImage = useStoreActions((actions) => actions.pluginModel.setIsAiModifyImage);
    const setIsDisable = useStoreActions((actions) => actions.editorModel.setIsDisable);
    const { showUserInfo } = useUserInfoHelper();

    const mainLayer = editor.active.getMainLayer();
    const activeLayer = editor.active.getPick(true) || mainLayer;

    useEffect(() => {
        // window._stat_track('tpbj_subfunction_click', {
        //     func: '30_121',
        // });
        window._stat_track('aisc_enter', {
            func: AI_MODIFY_IMAGE,
        });
        setAIModifyImageInfo(DefaultAIModifyImageInfo());
        setResultList([]);
        setGenerating(false);
        if (!activeLayer) {
            return;
        }
        const { layerType } = activeLayer;

        if (![LayerType.IMAGE_LAYER, LayerType.MAIN_LAYER].includes(layerType)) {
            return;
        }
        editor.imageLayer.showImageEffectRenderState(activeLayer.id, false);
        setIsAiModifyImage(true);
        setIsDisable(true);
        editor.disablePick(false);

        return () => {
            editor.enablePick();
            setIsDisable(false);
        };
    }, []);

    useEffect(() => {}, []);

    if (!activeLayer) {
        return null;
    }
    const { data, layerType } = activeLayer;

    if (![LayerType.IMAGE_LAYER, LayerType.MAIN_LAYER].includes(layerType)) {
        return null;
    }

    const exitClick = () => {
        if (generating) {
            return;
        }

        setAIModifyImageInfo(DefaultAIModifyImageInfo());
        setIsAiModifyImage(false);
        events.emit(PLUGIN_CLOSE_EVENT);
    };

    const apply = async (url: string) => {
        if (generating) {
            return;
        }
        setAIModifyImageInfo({
            ...aiModifyImageInfo,
            resultUrl: url,
            painType: AIModifyImagePainType.none,
        });
    };

    const done = async () => {
        try {
            // 如果多画布id重复可能有问题  概率极小极小
            funcUsedMapState[`aisc_${parseLayerId(activeLayer.id).id}`] = {
                ...funcUsedMapState[`aisc_${parseLayerId(activeLayer.id).id}`],
                [AI_MODIFY_IMAGE]: {
                    func: AI_MODIFY_IMAGE,
                    is_creative: true,
                    url: aiModifyImageInfo.resultUrl,
                },
            };
            setFuncUsedMapAction({
                ...funcUsedMapState,
            });
            await editor.imageLayer.processAIModifyImage(aiModifyImageInfo.resultUrl, activeLayer.id, true, '27', true);
            editor.active.set(activeLayer.id);
            exitClick();
        } catch (error) {
            console.error(error);
        }
    };

    const inputText = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setAIModifyImageInfo({
            ...aiModifyImageInfo,
            desc: e.target.value,
            resultUrl: '',
        });
    };

    const generateHandle = async () => {
        try {
            console.time('aiModify time');
            const startDate = new Date();
            window._stat_track('aisc_create_click', {
                func: AI_MODIFY_IMAGE,
                is_creative: true,
            });
            window._stat_track('tpbj_subfunction_tick', {
                func: '30_121',
            });
            const painMaskCanvas = document.getElementById('painMaskCanvas') as HTMLCanvasElement;

            if (!painMaskCanvas) {
                return;
            }

            setAIModifyImageInfo({
                ...aiModifyImageInfo,
                loading: true,
                resultUrl: '',
            });
            setGenerating(true);
            const { url } = data as IImageLayerProps;

            let realUrl = await base64Kit.getRemoteUrlAsync(url);
            const originImg = await loadImageAsync(realUrl);
            const originImageCanvas = document.createElement('canvas');
            originImageCanvas.width = originImg.width;
            originImageCanvas.height = originImg.height;
            const originImageCanvasCtx = originImageCanvas.getContext('2d');
            if (!originImageCanvasCtx) {
                return;
            }
            originImageCanvasCtx.fillStyle = '#fff';
            originImageCanvasCtx.fillRect(0, 0, originImg.width, originImg.height);
            originImageCanvasCtx.drawImage(originImg, 0, 0);
            const { url: originImgUrl } = await uploadBase64ToObs(originImageCanvas.toDataURL('image/png'));
            realUrl = originImgUrl;
            const canvas = document.createElement('canvas');
            canvas.width = originImg.width;
            canvas.height = originImg.height;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                return;
            }

            ctx.drawImage(originImg, 0, 0, originImg.width, originImg.height, 0, 0, canvas.width, canvas.height);

            ctx.fillStyle = '#000';
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            ctx.drawImage(painMaskCanvas, 0, 0);
            const result = await uploadBase64ToObs(canvas.toDataURL('image/png'));

            const rsp = await getAiModifyTask({
                parameter: {
                    rsp_media_type: 'jpg',
                    num_samples: 4,
                    prompt_pos: desc,
                },
                media_info_list: [
                    //  用户图像(全图，或者局部crop图)
                    {
                        media_data: realUrl,
                        media_extra: {},
                        media_profiles: {
                            media_data_type: 'url',
                        },
                    },
                    //  用户涂抹mask图
                    {
                        media_data: result.url,
                        media_extra: {},
                        media_profiles: {
                            media_data_type: 'url',
                        },
                    },
                ],
                extra: {},
            });

            const { msg_id } = rsp.data.data;

            const loopReq = async () => {
                try {
                    const res = await getAiTaskQuery({ msg_id });

                    if (res.data.code === 29901) {
                        setTimeout(() => {
                            loopReq();
                        }, 1000);
                    } else if (res.data.code === 0) {
                        const errorList: string[] = [];
                        if (res.data.data.media_info_list.length < 4) {
                            const img = await loadImageAsync(res.data.data.media_info_list[0].media_data);
                            new Array(4 - res.data.data.media_info_list.length)
                                .fill('')
                                .map((item) => errorList.push(`${img.width}-${img.height}`));
                        }
                        setResultList([
                            ...res.data.data.media_info_list.map((item: any) => item.media_data),
                            ...errorList,
                            ...resultList,
                        ]);
                        setAIModifyImageInfo({
                            ...aiModifyImageInfo,
                            loading: false,
                            resultUrl: res.data.data.media_info_list[0].media_data,
                            painType: AIModifyImagePainType.none,
                        });
                        setGenerating(false);
                        console.timeEnd('aiModify time');
                        window._stat_track('aisc_create_success', {
                            func: AI_MODIFY_IMAGE,
                            is_creative: true,
                        });

                        const endDate = new Date();
                        window._stat_track('aisc_create_duration', {
                            func: AI_MODIFY_IMAGE,
                            duration: endDate.getTime() - startDate.getTime(),
                        });
                    } else {
                        setAIModifyImageInfo({
                            ...aiModifyImageInfo,
                            loading: false,
                        });
                        setGenerating(false);
                        Message.error(res.data.msg || $t((v) => v.collapse.moreCollapse.imageTool.messageError.failed));
                    }
                } catch (error: any) {
                    setAIModifyImageInfo({
                        ...aiModifyImageInfo,
                        loading: false,
                    });

                    setGenerating(false);
                    Message.error(error.msg || $t((v) => v.collapse.moreCollapse.imageTool.messageError.failed));
                }
            };
            loopReq();
        } catch (error: any) {
            setAIModifyImageInfo({
                ...aiModifyImageInfo,
                loading: false,
            });

            setGenerating(false);
            Message.error(error.msg || $t((v) => v.collapse.moreCollapse.imageTool.messageError.failed));
        }
    };

    const paintTypeClick = (type: AIModifyImagePainType) => {
        setAIModifyImageInfo({
            ...aiModifyImageInfo,
            painType: type,
            resultUrl: '',
        });
    };

    const paintSizeChange = (value: number) => {
        setAIModifyImageInfo({
            ...aiModifyImageInfo,
            painType:
                aiModifyImageInfo.painType !== AIModifyImagePainType.none
                    ? aiModifyImageInfo.painType
                    : AIModifyImagePainType.pain,
            painSize: value,
            resultUrl: '',
        });
    };

    const resetClick = () => {
        if (generating) {
            return;
        }

        setAIModifyImageInfo({
            ...aiModifyImageInfo,
            resultUrl: '',
            hasPain: false,
        });
        const painMaskCanvas = document.getElementById('painMaskCanvas');
        const painCanvas = document.getElementById('painCanvas');
        painMaskCanvas && clearCanvas(painMaskCanvas as HTMLCanvasElement);
        painCanvas && clearCanvas(painCanvas as HTMLCanvasElement);
    };

    const downloadClick = (url: string) => {
        saveAs(
            url,
            `${$t((v) => v.editor.copyRight)}_${$t(
                (v) => v.collapse.moreCollapse.imageTool.aiModify.title,
            )}_${dayjs().format('YYYY.MM.DD')}.jpg`,
        );
        window._stat_track('aisc_download_success', {
            func: 'image_manipulation',
            is_creative: true,
            download_type: 'direct',
        });
    };

    return (
        <div className="aiModifyImage">
            <BackTitle
                handleClick={exitClick}
                title={$t((v) => v.collapse.moreCollapse.imageTool.aiModify.title)}
                iconName="QuestionMarkCircleBold"
                demoUrl={aiModifyImageMp4}
            />

            <div className="c-aiModifyImage-content">
                <Scrollbars autoHide>
                    <div className="title space-between">
                        <span>{$t((v) => v.collapse.moreCollapse.imageTool.aiModify.paintTitle)}</span>
                        <div className="reset" onClick={resetClick}>
                            <IconFont name="RefreshBold" color="var(--content-system-link, #38F)" fontSize={12} />
                            <span>{$t((v) => v.collapse.moreCollapse.imageTool.aiModify.reset)}</span>
                        </div>
                    </div>

                    <div className="pain">
                        <IconFont
                            name="ApplyBold"
                            fontSize={18}
                            onClick={() => paintTypeClick(AIModifyImagePainType.pain)}
                            color={
                                painType === AIModifyImagePainType.pain
                                    ? 'var(--content-system-link, #38F)'
                                    : 'var(--content-system-secondary)'
                            }
                        />
                        <div className="line" />
                        <IconFont
                            name="EraserBold"
                            onClick={() => paintTypeClick(AIModifyImagePainType.erase)}
                            fontSize={18}
                            color={
                                painType === AIModifyImagePainType.erase
                                    ? 'var(--content-system-link, #38F)'
                                    : 'var(--content-system-secondary)'
                            }
                        />
                        <div className="empty"></div>
                        <SliderInput
                            min={10}
                            max={100}
                            value={painSize}
                            disabled={false}
                            precision={1}
                            showStepBtn
                            type="horizontal"
                            onChange={(val: number) => {
                                paintSizeChange(val);
                            }}
                            onAfterChange={(val: number) => {}}
                            onClick={() => {}}
                        />
                    </div>

                    <div className="title">{$t((v) => v.collapse.moreCollapse.imageTool.aiModify.paintDescTitle)}</div>
                    <div className="input">
                        <textarea
                            placeholder={$t((v) => v.collapse.moreCollapse.imageTool.aiModify.tips.inputPlaceholder)}
                            value={desc}
                            disabled={generating}
                            onChange={(e) => inputText(e)}
                            maxLength={500}
                            onClick={() => {}}
                        />
                        {desc.length >= 500 ? (
                            <span className="red">
                                {$t((v) => v.collapse.moreCollapse.imageTool.aiModify.tips.inputLimit)}
                            </span>
                        ) : null}
                    </div>

                    {resultList.length > 0 ? (
                        <>
                            <div className="title">{$t((v) => v.collapse.moreCollapse.imageTool.result)}</div>
                            <div className="result-list">
                                {resultList.map((item) => {
                                    if (!item.includes('http')) {
                                        return (
                                            <ErrorItem
                                                key={item}
                                                width={item.split('-')[0]}
                                                height={item.split('-')[1]}
                                                text={$t((v) => v.collapse.moreCollapse.imageTool.aiModify.modify)}
                                            />
                                        );
                                    }
                                    return (
                                        <div className="img-wrap" key={item}>
                                            <div className="download-result-btn" onClick={() => downloadClick(item)}>
                                                <IconFont fontSize={12} name="DownloadBold" color="#fff" />
                                            </div>

                                            {/* {showLayerWaterMark ? (
                                                <div
                                                    className="watermark"
                                                    style={{
                                                        backgroundImage: `url(${waterMarkUrl})`
                                                    }}
                                                ></div>
                                            ) : null} */}

                                            <img
                                                onClick={() => apply(item)}
                                                className={classNames({
                                                    item: 'true',
                                                    active: item === aiModifyImageInfo.resultUrl,
                                                })}
                                                src={imageConverter(item, 360)}
                                                alt={$t((v) => v.collapse.moreCollapse.imageTool.image)}
                                                key={item}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    ) : null}
                </Scrollbars>
            </div>

            <div className="operate">
                <div className="operate-btn">
                    <div
                        className={classNames({
                            exit: true,
                            disable: generating,
                        })}
                        onClick={() => {
                            aiModifyImageInfo.resultUrl ? done() : exitClick();
                        }}
                    >
                        {aiModifyImageInfo.resultUrl
                            ? $t((v) => v.collapse.moreCollapse.imageTool.apply)
                            : $t((v) => v.collapse.moreCollapse.imageTool.exit)}
                    </div>

                    <div className="genBlock">
                        <div
                            className={classNames({
                                tip: true,
                                hide: generating || (aiModifyImageInfo.desc && aiModifyImageInfo.hasPain),
                            })}
                        >
                            {$t((v) => v.collapse.moreCollapse.imageTool.aiModify.tips.guide)}
                        </div>
                        <GenBtn
                            uuid={AI_MODIFY_IMAGE}
                            text={
                                generating ? (
                                    <div className="plugin-aiText-btns-loading">
                                        <IconFont fontSize={14} name={'LoadingCircleBlack'} />
                                        <div style={{ marginLeft: 4 }}>
                                            {$t((v) => v.collapse.moreCollapse.imageTool.generating)}
                                        </div>
                                    </div>
                                ) : resultList.length > 0 ? (
                                    $t((v) => v.collapse.moreCollapse.imageTool.reGenerate)
                                ) : (
                                    $t((v) => v.collapse.moreCollapse.imageTool.imGenerate)
                                )
                            }
                            style={{
                                width: 246,
                                opacity: generating || !aiModifyImageInfo.desc || !aiModifyImageInfo.hasPain ? 0.6 : 1,
                            }}
                            disabled={generating || !aiModifyImageInfo.desc || !aiModifyImageInfo.hasPain}
                            onGen={generateHandle}
                            onErrorMessage={(msg: string) => {
                                Message.error(msg);
                            }}
                            source={getCurrentScene()}
                            second_source="image_manipulation"
                        />
                    </div>
                </div>

                <AiTip />
            </div>
        </div>
    );
};

export default AIModifyImage;
